/**
 * Firebase Cloud Messaging Hook
 *
 * Handles FCM token registration, foreground message handling,
 * and service worker setup for web push notifications.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from '../firebase/config';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const REACT_APP_BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL || '';
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/**
 * Hook to manage Firebase Cloud Messaging for web push notifications
 *
 * @returns {Object} - { fcmToken, permissionStatus, requestPermission, unregisterToken }
 */
export const useFirebaseMessaging = () => {
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const messageListenerRef = useRef(null);
  const navigate = useNavigate();

  /**
   * Register the FCM token with the backend
   */
  const registerTokenWithBackend = useCallback(async (token) => {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        console.warn('[FCM] No access token, skipping backend registration');
        return false;
      }

      await axios.post(
        `${REACT_APP_BACKEND_API_URL}/api/device-token/register/`,
        {
          token: token,
          device_type: 'web',
          device_name: `${navigator.userAgent.split(' ').slice(-1)[0]} - Web`,
        },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('[FCM] Token registered with backend');
      return true;
    } catch (error) {
      console.error('[FCM] Failed to register token with backend:', error);
      return false;
    }
  }, []);

  /**
   * Unregister the FCM token from the backend (call on logout)
   */
  const unregisterToken = useCallback(async () => {
    if (!fcmToken) return;

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) return;

      await axios.post(
        `${REACT_APP_BACKEND_API_URL}/api/device-token/unregister/`,
        { token: fcmToken },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('[FCM] Token unregistered from backend');
      setFcmToken(null);
    } catch (error) {
      console.error('[FCM] Failed to unregister token:', error);
    }
  }, [fcmToken]);

  /**
   * Handle foreground messages - show toast if focused, OS notification if not
   */
  const handleForegroundMessage = useCallback(
    (payload) => {
      console.log('[FCM] Foreground message received:', payload);

      const title = payload.data?.title || payload.notification?.title || 'New Notification';
      const body = payload.data?.body || payload.notification?.body || '';
      const type = payload.data?.type;
      const channelId = payload.data?.channel_id;
      const messageId = payload.data?.message_id;
      const itemId = payload.data?.item_id;
      const commentId = payload.data?.comment_id;

      // Build URL path for navigation
      let url = '/';
      if (type === 'canvas_mention' && itemId) {
        // Canvas comment mention - navigate to item in focus mode with comment highlighted
        const commentParam = commentId ? `&commentId=${commentId}` : '';
        url = `/?itemId=${itemId}&focusMode=true${commentParam}`;
      } else if (channelId && messageId) {
        url = `/channels/${channelId}?messageId=${messageId}`;
      } else if (channelId) {
        url = `/channels/${channelId}`;
      }

      if (document.hasFocus()) {
        // User is actively using the app - show in-app toast
        toast.custom(
          (t) => (
            <div
              className={`${t.visible ? 'animate-enter' : 'animate-leave'
                } max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 cursor-pointer`}
              onClick={() => {
                toast.dismiss(t.id);
                navigate(url);
              }}
            >
              <div className="flex-1 w-0 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white text-lg">@</span>
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{body}</p>
                  </div>
                </div>
              </div>
              <div className="flex border-l border-gray-200 dark:border-gray-700">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.dismiss(t.id);
                  }}
                  className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 focus:outline-none"
                >
                  Close
                </button>
              </div>
            </div>
          ),
          {
            duration: 5000,
            position: 'top-right',
          }
        );
      } else {
        // Window not focused - show OS notification via Service Worker
        // This ensures clicks go through the same notificationclick handler
        if (Notification.permission === 'granted') {
          navigator.serviceWorker.ready.then((reg) => {
            const tag = messageId ? `msg-${messageId}` : (commentId ? `comment-${commentId}` : (type || 'fcm-notification'));
            reg.showNotification(title, {
              body: body,
              icon: '/notification-icon.png',
              tag: tag,
              data: { url, channel_id: channelId, message_id: messageId, type, item_id: itemId, comment_id: commentId }
            });
          });
        }
      }
    },
    [navigate]
  );

  /**
   * Register service worker and get FCM token
   */
  const initializeFCM = useCallback(async () => {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        console.warn('[FCM] This browser does not support notifications');
        return null;
      }

      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        console.warn('[FCM] Service workers not supported');
        return null;
      }

      // Get current permission status
      setPermissionStatus(Notification.permission);

      // If permission denied, don't proceed
      if (Notification.permission === 'denied') {
        console.warn('[FCM] Notification permission denied');
        return null;
      }

      // Get messaging instance
      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.warn('[FCM] Firebase Messaging not available');
        return null;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });
      console.log('[FCM] Service worker registered:', registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // If permission not yet granted, wait for user to request it
      if (Notification.permission === 'default') {
        console.log('[FCM] Notification permission not yet requested');
        return null;
      }

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        console.log('[FCM] Token obtained:', token.substring(0, 20) + '...');
        setFcmToken(token);

        // Register with backend
        await registerTokenWithBackend(token);

        // Set up foreground message listener
        if (!messageListenerRef.current) {
          messageListenerRef.current = onMessage(messaging, handleForegroundMessage);
        }

        return token;
      } else {
        console.warn('[FCM] No token received');
        return null;
      }
    } catch (error) {
      console.error('[FCM] Error initializing:', error);
      return null;
    }
  }, [registerTokenWithBackend, handleForegroundMessage]);

  /**
   * Request notification permission and initialize FCM
   */
  const requestPermission = useCallback(async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        console.log('[FCM] Notification permission granted');
        return await initializeFCM();
      } else {
        console.warn('[FCM] Notification permission not granted:', permission);
        return null;
      }
    } catch (error) {
      console.error('[FCM] Error requesting permission:', error);
      return null;
    }
  }, [initializeFCM]);

  // Initialize on mount if permission already granted
  useEffect(() => {
    const accessToken = Cookies.get('access_token');
    if (accessToken && Notification.permission === 'granted') {
      initializeFCM();
    }

    // Listen for navigation messages from service worker
    const handleServiceWorkerMessage = (event) => {
      console.log('[FCM] SW message received:', event.data);
      if (event.data?.type === 'NAVIGATE' && event.data?.url) {
        console.log('[FCM] Navigating to:', event.data.url);
        navigate(event.data.url);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [initializeFCM, navigate]);

  return {
    fcmToken,
    permissionStatus,
    requestPermission,
    unregisterToken,
    isSupported: 'Notification' in window && 'serviceWorker' in navigator,
  };
};

export default useFirebaseMessaging;

